import { Component, DoCheck, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ApiService, KeyHandlerService, RepositoryService, SecurityService, StorageService } from "@tylertech/core-module";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { ButtonMetadata, ILookupModifyProjectionParameter, IMetadataAction, IViewMetadata, PropertyMetadata, } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, RepositoryFormData, ValueChangedParms } from "../../../../interfaces";
import { CacheablePropertyMetadata, CodeLookupService, MetadataActionService, MetadataEventService, MetadataPropertyService, MetadataService, MetadataViewService, } from "../../../../services";
import { ObjectHelpers } from "../../../../utilities";
import { MetadataCreateEntityDialogComponent, MetadataCreateEntityDialogData } from "../../../metadataCreateEntityDialog/metadataCreateEntity.dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "metadata-text-button",
    templateUrl: "./metadata-text-button.tmpl.html",
    standalone: false
})
export class MetadataTextButtonComponent implements OnInit, DoCheck, OnDestroy
{
    private readonly matDialog = inject(MatDialog);
    private readonly $apiService = inject(ApiService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $storageService = inject(StorageService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $codeLookupService = inject(CodeLookupService);
    private readonly $repositoryService = inject(RepositoryService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);

    @Input()
    formRepository: RepositoryFormData;

    @Input()
    metadataModel: AugmentedModelObject;

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    viewOnly: boolean;

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    model: any;

    @Input()
    dense: boolean;

    @Input()
    compact: boolean;

    @Input()
    front: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    isMenuItem: boolean;

    @Input()
    forCellEdit: boolean;

    /** Accessible in the template */ isViewOnly: boolean;

    /** Accessible in the template */ isHidden: boolean;
    private _isViewOnly: boolean;
    private _isHidden: boolean;

    keyHandlerId: string;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        const self = this;
        this.keyHandlerId = Utilities.generateRandomGuid();
        $metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (self.metadataModel.$recalculateAfterRecalculate)
                    delete self.metadataModel.$recalculateAfterRecalculate;
                if (!args.Recalculating && !Utilities.IsNullOrUndefined(this.delayAction))
                    self.delayAction();
            });
    }

    ngOnInit(): void
    {
        if (!this.formRepository && !this.actionMetadata)
        {
            console.error(
                `${MetadataTextButtonComponent.name}|${this.propertyMetadata.Name}|RepositoryFormData binding was not set!`
            );
        }

        this.registerKeyboardShortcut(false);
    }

    ngOnDestroy()
    {
        this.$keyHandlerService.unsubscribeAllFromClass(this.keyHandlerId);
    }

    ngDoCheck(): void
    {
        const viewOnly: boolean =
            (this.isLinkedToControl ||
                ((this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) && (!this.actionMetadata || !this.actionMetadata.ViewOnlyEnabled) && this.propertyMetadata.InputType !== "Link") ||
                (this.actionMetadata &&
                    this.$metadataActionService.IsDisabled(this.actionMetadata, this.metadataModel, this.parent)) ||
                this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
                this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel)) ?? false;
        if (this._isViewOnly !== viewOnly)
        {
            this.registerKeyboardShortcut(viewOnly);
            this._isViewOnly = viewOnly;
        }
        this.isViewOnly = viewOnly;

        const isHidden =
            ((this.actionMetadata &&
                this.$metadataActionService.IsHidden(this.actionMetadata, this.metadataModel, this.parent)) ||
                this.$metadataPropertyService.isHidden(this.propertyMetadata, this.metadataModel, this.parent)) ?? false;
        if (this._isHidden !== isHidden)
        {
            this.registerKeyboardShortcut(isHidden);
            this._isHidden = isHidden;
        }
        this.isHidden = isHidden;
    }

    private get buttonMetadata(): ButtonMetadata
    {
        return this.propertyMetadata.ButtonMetadata;
    }

    private get actionMetadata(): IMetadataAction
    {
        return this.front
            ? this.propertyMetadata.ActionMetadataFront
            : this.propertyMetadata.ActionMetadata ?? this.propertyMetadata.LinkAction;
    }

    private get isLinkedToControl(): boolean
    {
        return !!this.buttonMetadata && this.buttonMetadata.IsLinkedToControl;
    }

    get hasIcon(): boolean
    {
        return this.$metadataPropertyService.hasIcon(this.propertyMetadata);
    }

    get ButtonIconName(): string
    {
        return this.$metadataPropertyService.getIcon(this.propertyMetadata, this.metadataModel, this.parent);
    }

    allowAdd(): boolean
    {
        return (
            !this.$metadataPropertyService.isDisabled(this.propertyMetadata, this.metadataModel, this.parent) &&
            this.$metadataPropertyService.isAllowAdd(this.propertyMetadata, this.metadataModel, this.parent)
        );
    }

    allowEdit(): boolean
    {
        return (
            !this.$metadataPropertyService.isDisabled(this.propertyMetadata, this.metadataModel, this.parent) &&
            this.$metadataPropertyService.isAllowEdit(this.propertyMetadata, this.metadataModel, this.parent)
        );
    }

    get Tooltip(): string
    {
        if (this.propertyMetadata.InputType === "Link") return this.Text;
        if (
            !Utilities.IsNullOrUndefined(this.propertyMetadata.ActionMetadata) &&
            !Utilities.IsNullOrUndefinedOrEmpty(this.propertyMetadata.ActionMetadata.Caption)
        )
            return this.propertyMetadata.ActionMetadata.Caption;
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.propertyMetadata.Hint)) return this.propertyMetadata.Hint;
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.propertyMetadata.HintProperty))
            return this.$metadataService.DependentModuleValue(
                this.propertyMetadata.HintProperty,
                this.parent,
                this.metadataModel
            );

        const tooltipText = this.Text;

        if (Utilities.IsNullOrUndefinedOrEmpty(tooltipText) && this.hasIcon)
            return this.propertyMetadata.Name;
        else
            return tooltipText;
    }

    get Text(): string
    {
        if (this.buttonMetadata && !Utilities.IsNullOrUndefinedOrEmpty(this.buttonMetadata.ButtonText)) return this.buttonMetadata.ButtonText;
        if (this.propertyMetadata.InputType === "Link") return this.metadataModel[this.propertyMetadata.Name];
        if (this.actionMetadata && this.actionMetadata.Label)
            return this.$metadataService.EvaluateString(this.actionMetadata.Label, this.parent, this.metadataModel);
        return this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get Icon(): string
    {
        return this.buttonMetadata ? this.buttonMetadata.ButtonIcon
            : this.actionMetadata.Icon ? this.actionMetadata.Icon : this.ButtonIconName;
    }

    delayAction: () => any;

    executeShortcutAction(): any
    {
        if (Utilities.IsNullOrUndefined(this.delayAction) && this.metadataModel.$recalculating)
        {
            this.delayAction = () => this.executeShortcutAction();
            return;
        }
        this.delayAction = undefined;
        return this.executeActionImpl();
    }

    executeAction($event: any = null, isAdd: boolean = false): any
    {
        $event.preventDefault();
        $event.stopPropagation();

        if (Utilities.IsNullOrUndefined(this.delayAction) && this.metadataModel.$recalculating)
        {
            this.metadataModel.$recalculateAfterRecalculate = true;
            this.delayAction = () => this.executeAction($event, isAdd);
            return;
        }
        this.delayAction = undefined;
        return this.executeActionImpl($event, isAdd);
    }

    executeActionImpl($event: any = null, isAdd: boolean = false): any
    {
        const self = this;
        if (this.actionMetadata)
        {
            let controller = this.formRepository;
            if (!this.actionMetadata.ServicePath)
            {
                controller = null;
            }
            if (this.viewOnly)
            {
                this.actionMetadata.ViewOnly = true;
            }
            else if (!Utilities.IsNullOrUndefined(this.actionMetadata.ViewToShow))
            {
                setFocusOnLoad(this.actionMetadata.ViewToShow);
            }
            return this.$metadataActionService
                .ExecuteAction(this.metadataModel, this.actionMetadata, controller)
                .then(updateModel);
        }
        else if (this.buttonMetadata)
        {
            if (!this.buttonMetadata.RepositoryType)
            {
                this.executeButtonAction(this.propertyMetadata, this.metadataModel)
                    .then((result: any) =>
                    {
                        $event?.srcElement?.focus();
                        this.fireValueChanged(result);
                    });
                return;
            }
            const odataFilter = this.createOdataFilter(20);
            return this.showDialogForButton(this.propertyMetadata, this.metadataModel, odataFilter)
                .then(updateModel);
        }
        else
        {
            if (self.propertyMetadata.Lookup)
            {
                const lookupModifyProjectionParameter = <ILookupModifyProjectionParameter>{
                    Type: self.propertyMetadata.Lookup,
                    HasCustomAdd: isAdd,
                };
                self.$codeLookupService
                    .GetLookupModifyProjection(lookupModifyProjectionParameter)
                    .then((action: IMetadataAction) =>
                    {
                        action.Label = self.propertyMetadata.Label;
                        action.Hidden = false;

                        const view = action.ViewToShow;
                        if (!Utilities.IsNullOrUndefined(action.FormToOpenInNewTab))
                        {
                            const idProperty = !Utilities.IsNullOrUndefinedOrEmpty(self.propertyMetadata.LookupLinkField)
                                ? self.propertyMetadata.LookupLinkField
                                : "Id";

                            if (!isAdd)
                            {
                                const builder = this.$odataFilterFactory.createBuilder().filterEQ(idProperty, self.model);
                                if (!Utilities.IsNullOrUndefined(action.FormToOpenInNewTab.Filter))
                                    builder.constantFilter(action.FormToOpenInNewTab.Filter?.replace("$filter=", ""));
                                action.FormToOpenInNewTab.Filter = builder.build();
                            }

                            else
                            {
                                if (!Utilities.IsNullOrUndefinedOrEmpty(self.propertyMetadata.AddNewTabFilter))
                                {
                                    const filter = this.$metadataService.EvaluateString(self.propertyMetadata.AddNewTabFilter, this.parent, this.metadataModel);
                                    action.FormToOpenInNewTab.Filter = "$filter=" + filter;
                                }



                                if (!action.FormToOpenInNewTab.Filter)
                                    action.FormToOpenInNewTab.Filter = "";
                                else
                                {
                                    action.FormToOpenInNewTab.Filter += "&";
                                }
                                action.FormToOpenInNewTab.Filter += "passback=" + idProperty;
                            }

                            const afterCloseTab: () => void = () =>
                            {
                                if (self.allowAdd() || self.allowEdit())
                                    self.propertyMetadata["$$forceRecalculate"] = true;
                                self.$metadataPropertyService.setBustCache(<CacheablePropertyMetadata>self.propertyMetadata);
                                let pb = self.$storageService.Get("passback");
                                if (pb)
                                {
                                    self.$storageService.Remove("passback");
                                    if (self.propertyMetadata.InputType === "MultiSelect")
                                        pb = [pb];
                                    self.fireValueChanged(pb);
                                }
                                else
                                {
                                    self.fireValueChanged(self.model);
                                }
                            };

                            self.$metadataActionService.OpenNewTabIfNeeded(self.model, action, afterCloseTab);
                            return;
                        }
                        else if (Utilities.IsNullOrUndefined(view))
                        {
                            // This is for the core to inject its own service calls at this point, so we can open core views instead of metadata views
                            self.$metadataActionService.ExecuteAction(self.model, action, null).then(updateModel);
                            return;
                        }

                        setFocusOnLoad(view);
                        const typeName = self.$metadataViewService.GetTypeName(view);
                        const odataFilter = self.createOdataFilter(20);
                        if (isAdd)
                        {
                            const model = self.model;
                            self.model = null;
                            self.$repositoryService
                                .GetNew(
                                    {
                                        Item1: typeName,
                                        Item2: model,
                                    },
                                    odataFilter
                                )
                                .then(result =>
                                {
                                    self.$metadataActionService
                                        .ExecuteAction(result, action, new RepositoryFormData(typeName, result))
                                        .then(m =>
                                        {
                                            updateModel(m);
                                            if (!Utilities.IsNullOrUndefined(m["canceledActionDialog"]))
                                                self.model = model;
                                        })
                                        .catch(() => self.fireValueChanged(null));
                                });
                        }
                        else if (self.model)
                        {
                            self.$repositoryService
                                .Get({
                                    Item1: typeName,
                                    Item2: self.model,
                                })
                                .then(result =>
                                {
                                    self.$metadataActionService.ExecuteAction(result, action, new RepositoryFormData(typeName, result))
                                        .then(updateModel);
                                });
                        }
                    });
            }
            else
                self.MaybeRunRecalculate(self.metadataModel, self.formRepository, $event?.srcElement);
        }

        function setFocusOnLoad(view: IViewMetadata): void
        {
            if (self.$metadataViewService.IsCombinedView(view))
                setFocusOnLoad(view["Views"][0]);
            else if (view.ViewMetaData?.Properties?.length > 0)
                view.ViewMetaData.Properties[0].FocusOnLoad = true;
        }

        function updateModel(updatedModel: any): void
        {
            $event?.srcElement?.focus();
            if (Utilities.IsNullOrUndefined(updatedModel)) return;
            if (!Utilities.IsNullOrUndefined(updatedModel["canceledActionDialog"])) return;
            const typeId = self.GetTypeNameId(updatedModel);
            const id = !Utilities.IsNullOrUndefined(updatedModel["Id"])
                ? updatedModel["Id"]
                : !Utilities.IsNullOrUndefined(updatedModel["ID"])
                    ? updatedModel["ID"]
                    : !Utilities.IsNullOrUndefined(typeId)
                        ? typeId
                        : !Utilities.IsNullOrUndefined(updatedModel["PropertyId"])
                            ? updatedModel["PropertyId"]
                            : updatedModel;
            if (self.buttonMetadata)
            {
                if (!Utilities.IsNullOrUndefined(self.buttonMetadata))
                {
                    const field = self.buttonMetadata.ChildLinkField || "Id";
                    if (field)
                    {
                        self.$metadataPropertyService.setBustCache(<CacheablePropertyMetadata>self.propertyMetadata);
                        self.fireValueChanged(updatedModel[field]);
                        return;
                    }
                }
                return;
            }
            if (self.propertyMetadata.InputType === "Select" || self.propertyMetadata.InputType === "TypeAhead")
            {
                self.$metadataPropertyService.setBustCache(<CacheablePropertyMetadata>self.propertyMetadata);
                const value = !Utilities.IsNullOrUndefinedOrEmpty(self.propertyMetadata.LookupLinkField)
                    ? updatedModel[self.propertyMetadata.LookupLinkField]
                    : id;
                self.model = value;
                if (self.propertyMetadata.AllowAdd || self.propertyMetadata.AllowEdit)
                    self.propertyMetadata["$$forceRecalculate"] = true;
                requestAnimationFrame(function ()
                {
                    self.fireValueChanged(value);
                });
            }
            else
            {
                //TODO: how does this work? it's passing a function reference as the model but fixing this breaks things that depend on it being broken
                const recalculated = self.MaybeRunRecalculate(updateModel, self.formRepository, $event?.srcElement);
                if (recalculated)
                {
                    return;
                }
                if (!ObjectHelpers.isDataCollection(updatedModel))
                {
                    //TODO what if this IS a dataCollection?
                    if (
                        id &&
                        !Utilities.IsNullOrUndefined(self.metadataModel.$$TypeName) &&
                        !Utilities.IsNullOrUndefined(self.propertyMetadata.RecalculateOnChange)
                    )
                        self.$metadataPropertyService.recalculateProperties(
                            self.metadataModel.$$TypeName,
                            self.propertyMetadata,
                            self.metadataModel,
                            self.parent,
                            id,
                            undefined,
                            true,
                            true
                        );
                }
            }
        }
    }

    private executeButtonAction(metadata: PropertyMetadata, model: any): Promise<any>
    {
        if (metadata.ButtonMetadata.FillServicePath)
        {
            const param = metadata.ButtonMetadata.ButtonParameter ? model : null;
            return this.$apiService.sendAuth(metadata.ButtonMetadata.FillServicePath, param);
        }
        return this.$codeLookupService.Get(metadata.Lookup);
    }


    private showDialogForButton(metadata: PropertyMetadata, model: any, odataFilter: string): Promise<any>
    {
        let parameter = model;
        if (metadata.ButtonMetadata.Data)
        {
            parameter = {};
            for (const x in metadata.ButtonMetadata.Data)
            {
                parameter[x] = metadata.ButtonMetadata.Data[x] === "{Value}" ? model : metadata.ButtonMetadata.Data[x];
            }
        }

        const dialogConfig: MatDialogConfig<MetadataCreateEntityDialogData> = {
            data: {
                type: metadata.ButtonMetadata.RepositoryType,
                parameter: Utilities.stringify(parameter),
                filter: odataFilter
            }
        };
        const dialogRef = this.matDialog.open(MetadataCreateEntityDialogComponent, dialogConfig);
        return dialogRef
            .afterClosed()
            .toPromise()
            .catch(_r =>
            {
                /* Squashing errors */
            });
    }

    private MaybeRunRecalculate(updatedModel: any, formData: RepositoryFormData, element: any): boolean
    {
        const self = this;
        const shouldRecalculate = !Utilities.IsNullOrUndefined(self.propertyMetadata.RecalculateOnChange);
        if (shouldRecalculate)
        {
            self.$metadataPropertyService.returnFocusToAfterRecalculate = element;
            let type = self.metadataModel.$$TypeName;
            if (!type && self.formRepository) type = self.formRepository.Type;
            self.$metadataPropertyService.recalculateProperties(
                type,
                self.propertyMetadata,
                self.metadataModel,
                self.parent,
                updatedModel[self.propertyMetadata.Name],
                undefined,
                true,
                true,
                formData
            );
        }
        return shouldRecalculate;
    }

    private GetTypeNameId(updatedModel: any): null
    {
        if (!Utilities.IsNullOrUndefined(updatedModel.$$TypeName))
        {
            const fullname = updatedModel.$$TypeName.split(".");
            const type = fullname[fullname.length - 1];
            if (!Utilities.IsNullOrUndefined(updatedModel[type + "Id"])) return updatedModel[type + "Id"];
        }
        return null;
    }

    private createOdataFilter(top: number): string
    {
        return this.$metadataPropertyService.createOdataFilter(
            this.propertyMetadata,
            this.metadataModel,
            this.parent,
            undefined,
            undefined,
            top
        );
    }

    fireValueChanged(value: any): void
    {
        this.onValueChanged.emit({ value: value, cd: true });

    }

    registerKeyboardShortcut(disabled: boolean): void
    {
        if (disabled)
        {
            if (this.propertyMetadata.ShortcutMetadata)
                this.$keyHandlerService.unsubscribe(this.propertyMetadata.ShortcutMetadata.Key, this.propertyMetadata.ShortcutMetadata.ModifierKey);
        }

        else
            this.$keyHandlerService.registerShortcutMetadata(this.keyHandlerId, this.propertyMetadata.ShortcutMetadata, `This Screen|${this.propertyMetadata.Label} button`, () => this.executeShortcutAction());
    }

    get isFullWidth(): boolean
    {
        return this.propertyMetadata?.FullWidth;
    }

    handleKeyDown(event: KeyboardEvent, link = false): void
    {
        if(event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
        else if(event.key === "Enter")
        {
            this.executeAction(event);
        }
    }
}
